import { useEffect, useState } from "react";
import { Camera, Pencil, Plus, Save, ToggleLeft, ToggleRight, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError, absolutePhotoUrl } from "@/lib/adminApi";
import {
  EmptyState,
  GhostButton,
  PageHeader,
  Panel,
  PrimaryButton,
} from "@/components/admin/uiKit";

const EMPTY = {
  name: "",
  town: "",
  quote: "",
  rating: 5,
  avatar_color: "#0EA5E9",
  order: 99,
  active: true,
  photo_url: "",
  photo_pending: false,
  object_position: "center",
};

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // testimonial object or "new"

  const load = () => {
    adminApi
      .get("/testimonials", { params: { limit: 60 } })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatAdminError(err)));
  };

  useEffect(load, []);

  const toggle = async (item) => {
    try {
      await adminApi.patch(`/admin/testimonials/${item.id}`, {
        active: !item.active,
      });
      toast.success(item.active ? "Hidden from site" : "Visible on site");
      load();
    } catch (err) {
      toast.error(formatAdminError(err));
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <PageHeader
        overline="Content"
        title="Testimonials"
        description="What your neighbors say. These power the homepage carousel and the /reviews page."
        actions={
          <PrimaryButton
            onClick={() => setEditing({ ...EMPTY })}
            data-testid="admin-testimonials-new"
          >
            <Plus size={14} /> New testimonial
          </PrimaryButton>
        }
      />

      {items === null ? (
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      ) : items.length === 0 ? (
        <EmptyState
          title="No testimonials yet"
          description="Add the first one to populate the carousel and reviews page."
          action={
            <PrimaryButton onClick={() => setEditing({ ...EMPTY })}>
              <Plus size={14} /> Add testimonial
            </PrimaryButton>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((t) => (
            <article
              key={t.id}
              className="rounded-xl bg-white border border-slate-200 overflow-hidden flex flex-col shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]"
              data-testid={`admin-testimonial-card-${t.id}`}
            >
              <div className="aspect-[3/2] bg-slate-100 relative">
                {t.photo_url ? (
                  <img
                    src={absolutePhotoUrl(t.photo_url)}
                    alt={t.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition: t.object_position || "center" }}
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-slate-400">
                    <Camera size={24} />
                  </div>
                )}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] bg-white/95 text-slate-700 px-2 py-1 rounded-md">
                  Order {t.order ?? "—"}
                </span>
                <button
                  type="button"
                  onClick={() => toggle(t)}
                  className={`absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-md transition-colors ${
                    t.active
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                  data-testid={`admin-testimonial-toggle-${t.id}`}
                >
                  {t.active ? (
                    <ToggleRight size={12} />
                  ) : (
                    <ToggleLeft size={12} />
                  )}
                  {t.active ? "Live" : "Hidden"}
                </button>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <p className="font-bold text-slate-900">{t.name}</p>
                <p className="text-xs text-slate-500 uppercase tracking-[0.18em] mt-0.5">
                  {t.town}
                </p>
                <p className="text-sm text-slate-700 mt-3 leading-relaxed line-clamp-3">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-auto pt-4 flex items-center gap-2">
                  <GhostButton onClick={() => setEditing(t)} data-testid={`admin-testimonial-edit-${t.id}`}>
                    <Pencil size={12} /> Edit
                  </GhostButton>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <TestimonialForm
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function TestimonialForm({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({ ...EMPTY, ...item });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.quote.trim() || !form.town.trim()) {
      toast.error("Name, town, and quote are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        town: form.town.trim(),
        quote: form.quote.trim(),
        rating: parseInt(form.rating, 10),
        avatar_color: form.avatar_color,
        order: parseInt(form.order, 10),
        active: !!form.active,
        photo_url: form.photo_url || null,
        photo_pending: !!form.photo_pending,
        object_position: form.object_position || "center",
      };
      if (isNew) {
        await adminApi.post("/admin/testimonials", payload);
        toast.success("Testimonial added");
      } else {
        await adminApi.patch(`/admin/testimonials/${item.id}`, payload);
        toast.success("Saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="admin-testimonial-form">
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
            {isNew ? "New testimonial" : "Edit testimonial"}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center"
          >
            <X size={16} />
          </button>
        </header>

        <div className="p-6 space-y-4">
          <FormRow label="Name">
            <input
              className="form-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              data-testid="admin-testimonial-input-name"
            />
          </FormRow>
          <FormRow label="Town">
            <input
              className="form-input"
              value={form.town}
              onChange={(e) => update("town", e.target.value)}
              data-testid="admin-testimonial-input-town"
            />
          </FormRow>
          <FormRow label="Quote">
            <textarea
              rows={5}
              className="form-input"
              value={form.quote}
              onChange={(e) => update("quote", e.target.value)}
              data-testid="admin-testimonial-input-quote"
            />
          </FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Rating">
              <select
                className="form-input"
                value={form.rating}
                onChange={(e) => update("rating", e.target.value)}
                data-testid="admin-testimonial-input-rating"
              >
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{r} stars</option>
                ))}
              </select>
            </FormRow>
            <FormRow label="Order">
              <input
                type="number"
                className="form-input"
                value={form.order}
                onChange={(e) => update("order", e.target.value)}
                data-testid="admin-testimonial-input-order"
              />
            </FormRow>
            <FormRow label="Photo URL">
              <input
                className="form-input"
                placeholder="/testimonials/xxx.jpg"
                value={form.photo_url || ""}
                onChange={(e) => update("photo_url", e.target.value)}
                data-testid="admin-testimonial-input-photo"
              />
            </FormRow>
            <FormRow label="Object position">
              <input
                className="form-input"
                placeholder="center / 50% 30%"
                value={form.object_position || ""}
                onChange={(e) => update("object_position", e.target.value)}
              />
            </FormRow>
            <FormRow label="Avatar color">
              <input
                type="color"
                className="form-input p-1"
                value={form.avatar_color}
                onChange={(e) => update("avatar_color", e.target.value)}
              />
            </FormRow>
            <FormRow label="Active">
              <label className="form-input flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.active}
                  onChange={(e) => update("active", e.target.checked)}
                />
                <span className="text-sm text-slate-700">Show on site</span>
              </label>
            </FormRow>
          </div>
          {form.photo_url && (
            <div className="rounded-lg overflow-hidden border border-slate-200 aspect-[3/2] bg-slate-100">
              <img
                src={absolutePhotoUrl(form.photo_url)}
                alt="preview"
                className="h-full w-full object-cover"
                style={{ objectPosition: form.object_position || "center" }}
              />
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center gap-2">
          <PrimaryButton onClick={save} disabled={saving} data-testid="admin-testimonial-save">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </PrimaryButton>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
        </footer>

        <style>{`
          .form-input {
            width: 100%;
            min-height: 2.75rem;
            border-radius: 0.5rem;
            border: 1px solid #E2E8F0;
            background: #fff;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            color: #0F172A;
          }
          .form-input:focus {
            outline: none;
            border-color: #38BDF8;
            box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
          }
        `}</style>
      </aside>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
